import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';
import { DATE_FORMAT_MM_DOT_DD_DOT_YYYY } from 'constants/formats';
import dayjs from 'dayjs';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessPlan,
  getIsFirstTryToSetBusinessToolTitle,
  getSetBusinessToolTitle,
} from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import { ReactComponent as FacebookIcon } from 'assets/icons/FacebookIcon.svg';
import { ReactComponent as InstagramIcon } from 'assets/icons/InstagramIcon.svg';
import { ReactComponent as LinkedInIcon } from 'assets/icons/LinkedInIcon.svg';

import { DatePickerControl } from 'components/_basic/Form/DatePicker';
import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { PhoneInputControlled } from 'components/_basic/Form/PhoneInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { FoundersRow } from 'components/BusinessToolForm/Sections/AboutCompany/GeneralInfo/components/FoundersRow';
import {
  ABOUT_COMPANY_GENERAL_NAMES,
  listOfFoundersInitialValues,
  prefix,
} from 'components/BusinessToolForm/Sections/AboutCompany/GeneralInfo/utils/form';
import { OperationIndustry } from 'components/BusinessToolForm/Sections/Global/components/OperationIndustry/OperationIndustry';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';

import { getValidatedMetadata } from 'helpers/metadataParser';
import { concatWithDot } from 'helpers/string';

export const GeneralInfoProject: React.FC = () => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const canAccess = true;
  const authUser = useUserStore(getAuthUser);

  const { id: businessPlanId = '' } = useParams();

  const updateMutation = useUpdateBusinessPlan();

  const isFirstTry = useBusinessToolStore(getIsFirstTryToSetBusinessToolTitle);
  const setBusinessToolTitle = useBusinessToolStore(getSetBusinessToolTitle);

  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const handleCompanyNameBlur = async () => {
    if (isFirstTry && authUser) {
      try {
        const companyName = getValues(concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME));

        const params = {
          id: businessPlanId,
          body: {
            title: companyName,
            metadata: JSON.stringify({
              ...getValidatedMetadata(businessPlan.metadata),
              isFirstTryToSetBusinessToolTitle: false,
            }),
          },
        };
        await updateMutation.mutateAsync(params);
        setBusinessToolTitle(companyName);
      } catch {
        return;
      }
    }
  };

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          title={t(`chapters.${FOR_INVESTORS_SECTIONS.COMPANY_AND_PROJECT}`)}
          badgeText={t('company.general.title')}
          locked={!canAccess}
          section={FOR_INVESTORS_SECTIONS.COMPANY_AND_PROJECT}
        />
      </FormSectionCard>
      <FormSectionCard
        title={t('company.general.briefCompanyDescription')}
        disabled={!canAccess}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
      >
        <TextInputControl
          multiline
          control={control}
          disabled={!canAccess}
          id="aboutCompany.general.briefCompanyDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
          minRows={3}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.name"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)}
              label={t('company.general.name')}
              maxLength={42}
              onBlur={handleCompanyNameBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.legalStructure"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.LEGAL_STRUCTURE)}
              label={t('company.general.legalStructure')}
              maxLength={150}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('company.general.industry')} disabled={!canAccess} collapsible>
        <OperationIndustry
          sectorId="company.general.companyDescription.sector"
          subSectorId="company.general.companyDescription.subsector"
          industryGroupId="company.general.companyDescription.industryGroup"
          industryId="company.general.companyDescription.industry"
          disabled={!canAccess}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <NumberInputControlled
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.numberOfEmployees"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.NUMBER_OF_EMPLOYEES)}
              label={t('company.general.numberOfEmployees')}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.address"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.ADDRESS)}
              label={t('company.general.address')}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePickerControl
              control={control}
              maxDate={dayjs().subtract(1, 'day')}
              disabled={!canAccess}
              id="aboutCompany.general.dateOfRegistration"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.REGISTRATION_DATE)}
              label={t('company.general.dateOfRegistration')}
              dateFormat={DATE_FORMAT_MM_DOT_DD_DOT_YYYY}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.websiteUrl"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.WEBSITE_URL)}
              label={t('company.general.websiteUrl')}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <PhoneInputControlled
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.phone"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.PHONE)}
              subName={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.PHONE_FORMAT)}
              label={t('company.general.phone')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.email"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.EMAIL)}
              label={t('company.general.email')}
              maxLength={150}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('company.general.networks')} disabled={!canAccess}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.facebook"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.FACEBOOK)}
              startAdornmentIcon={<FacebookIcon />}
              label="Facebook"
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.linkedin"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.LINKEDIN)}
              startAdornmentIcon={<LinkedInIcon />}
              label="Linkedin"
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.instagram"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.INSTAGRAM)}
              startAdornmentIcon={<InstagramIcon />}
              label="Instagram"
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.instagram"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.OTHER)}
              label={t('other')}
              maxLength={150}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('company.general.contactPerson')} disabled={!canAccess}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.firstName"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.FIRST_NAME)}
              label={t('company.general.firstName')}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.lastName"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.LAST_NAME)}
              label={t('company.general.lastName')}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.position"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.POSITION)}
              label={t('company.general.position')}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.contactPersonsEmail"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.CONTACT_PERSON_EMAIL)}
              label={t('company.general.contactPersonsEmail')}
              maxLength={150}
            />
          </Grid>
          <Grid item xs={6}>
            <PhoneInputControlled
              control={control}
              disabled={!canAccess}
              id="aboutCompany.general.contactPersonPhone"
              name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.CONTACT_PERSON_PHONE)}
              subName={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.CONTACT_PERSON_PHONE_FORMAT)}
              label={t('company.general.contactPersonPhone')}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('company.general.listOfFounders')} disabled={!canAccess} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, ABOUT_COMPANY_GENERAL_NAMES.LIST_OF_FOUNDERS)}
          initialItem={listOfFoundersInitialValues}
          buttonText="Founder"
          disabled={!canAccess}
        >
          {(namePrefix) => <FoundersRow name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
    </>
  );
};
